/**
 * Turning a heightmap into a tile mesh.
 *
 * Kept free of any DOM or network dependency so it runs in the worker, in Node and in tests
 * alike — the geometry is the part most worth testing and the part hardest to eyeball, since a
 * winding-order or skirt bug looks like "the terrain is a bit odd from certain angles".
 *
 * ## The three things this has to get right
 *
 * **Curvature.** Vertices are built in a true tangent frame anchored at the tile centre, from
 * exact geodetic positions. The Earth's curve is therefore *in the geometry*: a 40 km tile is
 * genuinely domed, and the horizon dips because the ground falls away, not because a shader
 * bent it afterwards. It also means a tile is a rigid body — the floating origin re-places it
 * with a matrix instead of re-meshing it.
 *
 * **Skirts.** Neighbouring tiles stream in independently and settle at different LODs, so their
 * shared edge does not match and a hairline of sky shows through. Every tile therefore carries
 * a vertical curtain hanging from its border. Stitching the edges properly would need every
 * neighbour's LOD at mesh time, which is exactly what a streaming quadtree cannot promise.
 *
 * **Geomorphing.** Swapping a tile for its parent moves every other vertex, and the ground
 * visibly jumps. Each vertex therefore also carries the height it would have on the next
 * coarser grid, and the material blends between the two as a tile approaches its switch — the
 * detail fades in instead of appearing.
 *
 * ## What is *not* per tile
 *
 * The texture coordinates and the triangle list depend on nothing but `gridSize`. Every tile in
 * the world has byte-identical copies of both — 36 KB of UVs and 104 KB of indices each — so
 * building them per tile meant allocating them in the worker, transferring them across the
 * boundary, and then keeping ~140 KB of duplicate typed array alive on the JS heap for every
 * resident tile, forever, because three has no way to release a vertex buffer's CPU copy after
 * upload (`onUploadCallback` is commented out in both the WebGPU backend and its WebGL2
 * fallback). With a few hundred tiles resident that is most of a hundred megabytes of the same
 * two arrays over and over.
 *
 * They are therefore built once, by {@link tileUvs} and {@link tileIndices}, and shared. Each
 * tile still gets its own `BufferAttribute` object wrapping the shared array — three keys GPU
 * buffers and their disposal by attribute *object*, so per-tile attributes over one shared array
 * give each tile its own GPU buffer (which it must have) while the CPU pays for exactly one.
 *
 * `updateFixed` / `updateFrame`: neither. Called once per tile, in a worker.
 */

import { Vector3 } from 'three';
import { LocalFrame } from '../geodesy.js';
import { tileXToLongitude, tileYToLatitude } from './tileId.js';
import { tileIndexCount, tileVertexCount } from './tileMessages.js';

export interface BuildTileMeshParams {
  zoom: number;
  x: number;
  y: number;
  /** Vertices along each tile edge. Must be odd so the morph lattice has a centre. */
  gridSize: number;
  /** How far the edge curtain hangs down [m]. */
  skirtDepthM: number;
  /**
   * Elevation samples [m], `demSize × demSize`, row-major starting at the north-west corner.
   * Null builds a flat tile at sea level, which is what an ocean tile or a failed fetch gets.
   */
  heights: Float32Array | null;
  demSize: number;
  /**
   * The window of {@link heights} this tile covers, in normalised raster coordinates.
   *
   * `(0, 0, 1)` — the default — means the height field is this tile's own DEM tile, which is the
   * case at every zoom the elevation source actually publishes. Past that cap the tile is handed
   * its nearest *ancestor's* DEM and a sub-rectangle of it: at two levels down, sixteen tiles
   * share one raster and each reads a quarter-by-quarter window of it.
   *
   * Why that is worth doing rather than simply stopping: elevation and imagery run out at
   * different depths, and binding the quadtree to the shallower of the two threw away the deeper
   * one. Terrarium stops at z15 and NAIP is a 1 m source good to z17, so the ground was being
   * drawn at 3.78 m/px from imagery that had 0.95 m/px available. Interpolating elevation past
   * its last real posting invents no relief — but it invents no *error* either, because SRTM's
   * 30 m posting was already being upsampled by the time it reached z15. Imagery past its last
   * real posting is exactly the same bargain, and we already take it (see `EoxImageryProvider`).
   */
  demU0?: number;
  demV0?: number;
  demSpan?: number;
}

export interface TileMeshData {
  positions: Float32Array;
  normals: Float32Array;
  morphHeights: Float32Array;
  /** The sampled height field [m MSL], `gridSize × gridSize`, row-major from the north-west. */
  heightGrid: Float32Array;
  minHeightM: number;
  maxHeightM: number;
  waterFraction: number;
  /** Half the diagonal of the tile's bounding box [m] — the culling radius. */
  boundingRadiusM: number;
}

/**
 * Texture coordinates for a tile grid of this size. Shared across every tile — see the note at
 * the top of this file. Row 0 of the imagery is the tile's north edge and three samples `v`
 * upward from the bottom, so `v` is flipped.
 *
 * The returned array is cached and **must not be mutated**.
 */
export function tileUvs(gridSize: number): Float32Array {
  const cached = uvCache.get(gridSize);
  if (cached !== undefined) return cached;

  const last = gridSize - 1;
  const uvs = new Float32Array(tileVertexCount(gridSize) * 2);
  for (let j = 0; j < gridSize; j++) {
    for (let i = 0; i < gridSize; i++) {
      const vi = j * gridSize + i;
      uvs[vi * 2] = i / last;
      uvs[vi * 2 + 1] = 1 - j / last;
    }
  }
  // The skirt vertices take their UV from the edge vertex they hang beneath, so the curtain is
  // the edge pixel stretched downward rather than a differently-coloured band.
  let skirtVertex = gridSize * gridSize;
  for (let e = 0; e < 4; e++) {
    for (let k = 0; k < gridSize; k++) {
      const source = edgeVertexIndex(e, k, gridSize);
      const vi = skirtVertex++;
      uvs[vi * 2] = uvs[source * 2];
      uvs[vi * 2 + 1] = uvs[source * 2 + 1];
    }
  }

  uvCache.set(gridSize, uvs);
  return uvs;
}

/**
 * The triangle list for a tile grid of this size — surface first, then the four skirt curtains,
 * in the order {@link tileUvs} lays the skirt vertices out. Shared across every tile.
 *
 * `Uint32Array` by default because it is what the WebGPU backend wants: three converts a
 * `Uint16Array` index buffer to `Uint32Array` on upload and *reassigns the attribute's array*,
 * so a 16-bit index list saves nothing there and costs one 104 KB allocation and copy per tile.
 * The WebGL2 fallback uses 16-bit indices natively and genuinely halves its index memory, so it
 * asks for `Uint16Array` — see `TerrainSystemOptions.indexType`.
 *
 * The returned array is cached and **must not be mutated**.
 */
export function tileIndices(gridSize: number, use16Bit = false): Uint16Array | Uint32Array {
  const cache = use16Bit ? index16Cache : index32Cache;
  const cached = cache.get(gridSize);
  if (cached !== undefined) return cached;

  const last = gridSize - 1;
  const count = tileIndexCount(gridSize);
  const indices = use16Bit ? new Uint16Array(count) : new Uint32Array(count);

  let n = 0;
  for (let j = 0; j < last; j++) {
    for (let i = 0; i < last; i++) {
      const a = j * gridSize + i;
      const b = a + 1;
      const c = a + gridSize;
      const d = c + 1;
      // Counter-clockwise seen from above, which is front-facing for three's default culling.
      indices[n++] = a;
      indices[n++] = c;
      indices[n++] = b;
      indices[n++] = b;
      indices[n++] = c;
      indices[n++] = d;
    }
  }

  for (let e = 0; e < 4; e++) {
    const first = gridSize * gridSize + e * gridSize;
    // Wind each curtain so it faces outward from the tile.
    const outward = e === 0 || e === 3;
    for (let k = 0; k < last; k++) {
      const topA = edgeVertexIndex(e, k, gridSize);
      const topB = edgeVertexIndex(e, k + 1, gridSize);
      const bottomA = first + k;
      const bottomB = first + k + 1;
      if (outward) {
        indices[n++] = topA;
        indices[n++] = bottomA;
        indices[n++] = topB;
        indices[n++] = topB;
        indices[n++] = bottomA;
        indices[n++] = bottomB;
      } else {
        indices[n++] = topA;
        indices[n++] = topB;
        indices[n++] = bottomA;
        indices[n++] = topB;
        indices[n++] = bottomB;
        indices[n++] = bottomA;
      }
    }
  }

  (cache as Map<number, Uint16Array | Uint32Array>).set(gridSize, indices);
  return indices;
}

/** Indices into the surface grid along edge `e` (north, south, west, east), position `k`. */
function edgeVertexIndex(edge: number, k: number, gridSize: number): number {
  switch (edge) {
    case 0:
      return k; // north, j = 0
    case 1:
      return (gridSize - 1) * gridSize + k; // south
    case 2:
      return k * gridSize; // west
    default:
      return k * gridSize + (gridSize - 1); // east
  }
}

/** Triangles in the surface grid alone, skirts excluded — where the skirt indices begin. */
export function tileSurfaceIndexCount(gridSize: number): number {
  return (gridSize - 1) * (gridSize - 1) * 6;
}

const uvCache = /* #__PURE__ */ new Map<number, Float32Array>();
const index16Cache = /* #__PURE__ */ new Map<number, Uint16Array>();
const index32Cache = /* #__PURE__ */ new Map<number, Uint32Array>();

/**
 * Sample the DEM with bilinear filtering, in normalised tile coordinates.
 *
 * Bilinear rather than nearest because the grid is usually coarser than the DEM: at 33 vertices
 * across a 256-pixel tile we are taking every eighth sample, and nearest-neighbour picks one
 * arbitrary pixel out of each 8×8 block. On a ridge line that means the ridge exists or does
 * not depending on where the samples happen to land, and the terrain shimmers between LODs.
 */
export function sampleHeightBilinear(
  heights: Float32Array,
  demSize: number,
  u: number,
  v: number,
): number {
  const fx = Math.min(Math.max(u, 0), 1) * (demSize - 1);
  const fy = Math.min(Math.max(v, 0), 1) * (demSize - 1);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = Math.min(x0 + 1, demSize - 1);
  const y1 = Math.min(y0 + 1, demSize - 1);
  const tx = fx - x0;
  const ty = fy - y0;

  const h00 = heights[y0 * demSize + x0];
  const h10 = heights[y0 * demSize + x1];
  const h01 = heights[y1 * demSize + x0];
  const h11 = heights[y1 * demSize + x1];

  return (h00 * (1 - tx) + h10 * tx) * (1 - ty) + (h01 * (1 - tx) + h11 * tx) * ty;
}

/**
 * The height this vertex would have on the next coarser grid.
 *
 * The parent's grid over this tile is exactly the even-indexed lattice of ours, so a vertex at
 * an even index keeps its height and one at an odd index is the average of its even neighbours
 * — linear along an edge, bilinear at the centre of a cell. Deriving it from our own samples
 * rather than fetching the parent tile means geomorphing costs one extra attribute and no
 * extra network traffic at all.
 */
function coarseHeight(grid: Float32Array, gridSize: number, i: number, j: number): number {
  const at = (ii: number, jj: number): number => grid[jj * gridSize + ii];
  const iOdd = (i & 1) === 1;
  const jOdd = (j & 1) === 1;
  if (!iOdd && !jOdd) return at(i, j);

  // Clamp at the border: the last index is odd when gridSize is even, and a tile edge has no
  // neighbour to average with on the outside.
  const iLo = iOdd ? i - 1 : i;
  const iHi = iOdd ? Math.min(i + 1, gridSize - 1) : i;
  const jLo = jOdd ? j - 1 : j;
  const jHi = jOdd ? Math.min(j + 1, gridSize - 1) : j;

  if (iOdd && !jOdd) return (at(iLo, j) + at(iHi, j)) * 0.5;
  if (!iOdd && jOdd) return (at(i, jLo) + at(i, jHi)) * 0.5;
  return (at(iLo, jLo) + at(iHi, jLo) + at(iLo, jHi) + at(iHi, jHi)) * 0.25;
}

export function buildTileMesh(params: BuildTileMeshParams): TileMeshData {
  const { zoom, x, y, gridSize, skirtDepthM, heights, demSize } = params;
  const last = gridSize - 1;
  const demU0 = params.demU0 ?? 0;
  const demV0 = params.demV0 ?? 0;
  const demSpan = params.demSpan ?? 1;

  // The tile's own tangent frame, anchored at its centre. Everything below is relative to it.
  const centreLon = tileXToLongitude(x + 0.5, zoom);
  const centreLat = tileYToLatitude(y + 0.5, zoom);
  const frame = new LocalFrame(centreLat, centreLon, 0);

  // --- sample the height field ------------------------------------------------------------
  const grid = new Float32Array(gridSize * gridSize);
  let minHeightM = Infinity;
  let maxHeightM = -Infinity;
  let waterSamples = 0;

  for (let j = 0; j < gridSize; j++) {
    const v = demV0 + (j / last) * demSpan;
    for (let i = 0; i < gridSize; i++) {
      const h =
        heights === null
          ? 0
          : sampleHeightBilinear(heights, demSize, demU0 + (i / last) * demSpan, v);
      grid[j * gridSize + i] = h;
      if (h < minHeightM) minHeightM = h;
      if (h > maxHeightM) maxHeightM = h;
      if (h <= 0) waterSamples++;
    }
  }
  if (heights === null) {
    minHeightM = 0;
    maxHeightM = 0;
  }

  // --- vertices ------------------------------------------------------------------------------
  const vertexCount = tileVertexCount(gridSize);
  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const morphHeights = new Float32Array(vertexCount);

  // One scratch vector for the whole loop — this is the worker's hot path, and a tile is a
  // thousand vertices.
  const scratch = new Vector3();

  for (let j = 0; j < gridSize; j++) {
    // Latitude is *not* linear in tile y — that is the whole point of Mercator — so each row
    // gets its true latitude rather than a lerp between the tile's north and south edges.
    const latitudeDeg = tileYToLatitude(y + j / last, zoom);
    for (let i = 0; i < gridSize; i++) {
      const longitudeDeg = tileXToLongitude(x + i / last, zoom);
      const height = grid[j * gridSize + i];

      frame.geodeticToTangent(latitudeDeg, longitudeDeg, height, scratch);

      const vi = j * gridSize + i;
      // Tangent ENU (east, north, up) → render (x east, y up, z south).
      positions[vi * 3] = scratch.x;
      positions[vi * 3 + 1] = scratch.z;
      positions[vi * 3 + 2] = -scratch.y;

      morphHeights[vi] = coarseHeight(grid, gridSize, i, j);
    }
  }

  // --- normals, from the geometry rather than the height field ----------------------------------
  // Central differences on the heightmap would need the true east/north spacing at every row,
  // which varies with latitude; taking the cross product of the actual neighbouring vertices
  // gets that for free and is correct on a curved tile as well.
  accumulateNormals(positions, tileIndices(gridSize), tileSurfaceIndexCount(gridSize), normals);

  // --- skirts -------------------------------------------------------------------------------------
  // Positions and normals only: the skirt's UVs and triangles are part of the shared grid, and
  // {@link tileUvs} and {@link tileIndices} lay them out in exactly this order.
  let skirtVertex = gridSize * gridSize;
  for (let e = 0; e < 4; e++) {
    for (let k = 0; k < gridSize; k++) {
      const source = edgeVertexIndex(e, k, gridSize);
      const vi = skirtVertex++;
      positions[vi * 3] = positions[source * 3];
      positions[vi * 3 + 1] = positions[source * 3 + 1] - skirtDepthM;
      positions[vi * 3 + 2] = positions[source * 3 + 2];
      // The curtain takes the edge's normal so it shades like the ground it hangs from — a
      // skirt lit as a separate surface is more obvious than the crack it is hiding.
      normals[vi * 3] = normals[source * 3];
      normals[vi * 3 + 1] = normals[source * 3 + 1];
      normals[vi * 3 + 2] = normals[source * 3 + 2];
      morphHeights[vi] = morphHeights[source] - skirtDepthM;
    }
  }

  return {
    positions,
    normals,
    morphHeights,
    heightGrid: grid,
    minHeightM,
    maxHeightM,
    waterFraction: waterSamples / (gridSize * gridSize),
    // The skirt hangs below everything else, so it sets the culling radius.
    boundingRadiusM: boundingRadiusOf(positions, gridSize * gridSize) + skirtDepthM,
  };
}

/** Area-weighted vertex normals from the surface triangles only (skirts get the edge's). */
function accumulateNormals(
  positions: Float32Array,
  indices: Uint16Array | Uint32Array,
  indexCount: number,
  out: Float32Array,
): void {
  for (let t = 0; t < indexCount; t += 3) {
    const a = indices[t] * 3;
    const b = indices[t + 1] * 3;
    const c = indices[t + 2] * 3;

    const abx = positions[b] - positions[a];
    const aby = positions[b + 1] - positions[a + 1];
    const abz = positions[b + 2] - positions[a + 2];
    const acx = positions[c] - positions[a];
    const acy = positions[c + 1] - positions[a + 1];
    const acz = positions[c + 2] - positions[a + 2];

    // Not normalised: the cross product's length is twice the triangle area, which weights
    // each face by its size and is what makes normals correct on an irregular grid.
    const nx = aby * acz - abz * acy;
    const ny = abz * acx - abx * acz;
    const nz = abx * acy - aby * acx;

    out[a] += nx;
    out[a + 1] += ny;
    out[a + 2] += nz;
    out[b] += nx;
    out[b + 1] += ny;
    out[b + 2] += nz;
    out[c] += nx;
    out[c + 1] += ny;
    out[c + 2] += nz;
  }

  for (let v = 0; v < out.length; v += 3) {
    const length = Math.hypot(out[v], out[v + 1], out[v + 2]);
    if (length > 1e-12) {
      out[v] /= length;
      out[v + 1] /= length;
      out[v + 2] /= length;
    } else {
      out[v + 1] = 1; // degenerate triangle; point it at the sky rather than leave it zero
    }
  }
}

function boundingRadiusOf(positions: Float32Array, vertexCount: number): number {
  let maxSq = 0;
  for (let v = 0; v < vertexCount; v++) {
    const sq =
      positions[v * 3] ** 2 + positions[v * 3 + 1] ** 2 + positions[v * 3 + 2] ** 2;
    if (sq > maxSq) maxSq = sq;
  }
  return Math.sqrt(maxSq);
}

/**
 * Decode a terrarium RGBA buffer into metres.
 *
 * `height_m = R·256 + G + B/256 − 32768`, straight from the spec in CLAUDE.md. Alpha is
 * ignored; the encoding uses all three colour channels and nothing else.
 */
export function decodeTerrariumRgba(rgba: Uint8ClampedArray | Uint8Array, out: Float32Array): Float32Array {
  for (let i = 0, p = 0; i < out.length; i++, p += 4) {
    out[i] = rgba[p] * 256 + rgba[p + 1] + rgba[p + 2] / 256 - 32768;
  }
  return out;
}
